// Top-of-dashboard task / quick-link tiles. Visibility is gated by the real company module
// (useSecuredFlag → hasModule) combined with the availability of the section's data from the
// dashboard API, and each cluster is wrapped in SecuredArea so it self-gates (and is inspectable
// in security mode). Counts link into the relevant list with filters preselected; the summary
// row also offers "+ New" creation links.
import React, { useContext, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { ApiContext } from '@api/api';
import { CarrierExpeditionStatusEnum, CarrierTasksDTO, CompanyModuleEnum, ConsigneeTasksDTO, EntityTypeEnum, PseudoStatesEnum, StatusEnumsEnum, TasksAvailabilityDTO } from '@api/ringil-api.v2.tsx';
import { isNotNullOrUndefined } from '@utils/utils.tsx';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';
import useNavigation from '@hooks/useNavigation.tsx';
import useWorkflowStates from '@hooks/useWorkFlowStates/useWorkflowStates.tsx';
import { useSecuredFlag } from '@components/security/useSecuredFlag';
import SecuredArea from '@components/security/SecuredArea';
import { PermissionTypesEnum } from '@api/ringil-api.v2.tsx';
import Heading from '@components/ringil3/Heading/Heading.tsx';
import { useCreateTimeslotSidebar } from '@components/ringil3/Features/Reservation/Sidedbars/CreateTimeslotSidebar.tsx';
import DashboardCountTile from '../components/DashboardCountTile.tsx';
import DashboardSummaryTile from '../components/DashboardSummaryTile.tsx';
import { ArchivedFilterStateEnum } from '@app/pages/customer/expedition-list/ResponsiveExpeditionList.tsx';

interface TaskTilesProps {
  availability: TasksAvailabilityDTO;
}

// The reservation "open" tiles (yesterday/today/tomorrow) and the "Open reservations" summary
// count reservations excluding the final workflow states — keep the list links in sync so the
// linked list matches the count. Timeslot workflow states are company-configurable, so the link's
// `states` filter (a positive include-list) is derived as all live states minus these.
// Compared case-insensitively because the backend state names are lowercase ('finished', 'exit').
const RESERVATION_CLOSED_STATES = ['finished', 'exit'];

const TaskTiles: React.FC<TaskTilesProps> = ({ availability }) => {
  const { tr } = useTranslationLgs();
  const nav = useNavigation();
  const { loggedUser } = useContext(AuthenticatedUserContext);
  const { getDashboardSectionConsigneeTasks, getDashboardSectionCarrierTasks } = useContext(ApiContext);
  // New reservations are created via a sidebar (there is no standalone create page).
  const { CreateTimeslotSidebar, createTimeslotRequest, setCreateTimeslotRequest } = useCreateTimeslotSidebar();
  // Live timeslot workflow states → "open" include-list (all minus the final states) for list links.
  const timeslotStates = useWorkflowStates(EntityTypeEnum.TIMESLOT);
  const openReservationStates = timeslotStates.filter(s => !RESERVATION_CLOSED_STATES.includes(s.toLowerCase()));

  const [consignee, setConsignee] = useState<ConsigneeTasksDTO>();
  const [carrier, setCarrier] = useState<CarrierTasksDTO>();

  const consigneeAvailable = availability.consignee.expedition || availability.consignee.reservation || availability.consignee.supplierNotification;
  const carrierAvailable = availability.carrier.expedition || availability.carrier.reservation || availability.carrier.supplierNotification;

  useEffect(() => {
    if (consigneeAvailable) getDashboardSectionConsigneeTasks(data => setConsignee(data));
    if (carrierAvailable) getDashboardSectionCarrierTasks(data => setCarrier(data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Visibility = real company module (hasModule) AND the availability flag (module + PERMISSION_DASHBOARD_*_TASKS
  // permission, RD-2682) AND the section's data being present.
  const expeditionFlag = useSecuredFlag('dashboard-tasks-expedition', {
    description: 'Consignee expedition tasks',
    modules: [{ module: CompanyModuleEnum.EXPEDITION }],
    data: [
      { label: 'expedition tasks available', satisfied: availability.consignee.expedition },
      { label: 'expedition tasks loaded', satisfied: isNotNullOrUndefined(consignee?.expeditions) },
    ],
  });
  const reservationFlag = useSecuredFlag('dashboard-tasks-reservation', {
    description: 'Consignee reservation tasks',
    modules: [{ module: CompanyModuleEnum.TIMESLOTS }],
    data: [
      { label: 'reservation tasks available', satisfied: availability.consignee.reservation },
      { label: 'reservation tasks loaded', satisfied: isNotNullOrUndefined(consignee?.reservations) },
    ],
  });
  const notificationFlag = useSecuredFlag('dashboard-tasks-supplier-notification', {
    description: 'Consignee supplier-notification tasks',
    modules: [{ module: CompanyModuleEnum.SUPPLIER_NOTIFICATION }],
    data: [
      { label: 'notification tasks available', satisfied: availability.consignee.supplierNotification },
      { label: 'notification tasks loaded', satisfied: isNotNullOrUndefined(consignee?.notificationsWithoutExpedition) },
    ],
  });
  // Carrier tiles are all expedition tasks (demands + shipments) -> gated by the carrier expedition availability.
  const carrierFlag = useSecuredFlag('dashboard-tasks-carrier', {
    description: 'Carrier tasks',
    modules: [{ module: CompanyModuleEnum.CARRIER }],
    data: [
      { label: 'carrier tasks available', satisfied: availability.carrier.expedition },
      { label: 'carrier tasks visible', satisfied: !!carrier?.visible },
    ],
  });

  // The expedition tiles ("My shipments") are scoped to the logged-in user as responsible person,
  // matching the legacy ModuleDashboard (responsiblePersonIds=loggedUser.id). The "Active
  // expeditions" summary is the sum of the per-status breakdown, so its link is scoped the same way
  // (union of the breakdown statuses) to keep the count and the linked list consistent.
  const ACTIVE_EXPEDITION_STATUSES = [
    StatusEnumsEnum.ASSIGNING,
    StatusEnumsEnum.WAITING_FOR_DOCS,
    StatusEnumsEnum.NEW,
    StatusEnumsEnum.ACTIVE,
    StatusEnumsEnum.READY_TO_SHIPMENT,
    StatusEnumsEnum.ASSIGNED,
  ];
  // archivedState=ALL so the linked list shows both archived and non-archived shipments (the
  // expedition list otherwise defaults to non-archived only), matching the archive-agnostic counts.
  const expLink = (statusEnums: StatusEnumsEnum[]) =>
    nav.createNavigationLink(nav.urlFunctions.createCustomerExpeditionList({ responsiblePersonIds: loggedUser?.id, statusEnums, archivedState: ArchivedFilterStateEnum.ALL }));
  const activeExpeditionsLink = expLink(ACTIVE_EXPEDITION_STATUSES);

  // Reservation list links filtered to a calendar day (ISO datetime range, matching the list's
  // timeslotDaySince/Till calendar filter). Summary spans yesterday→tomorrow.
  // Exclude the final states so the list matches the tile count. Omitted until workflow states
  // load (an empty include-list would wrongly filter the list down to nothing).
  const openStatesParam = openReservationStates.length ? { states: openReservationStates } : {};
  const reservationDayLink = (dayOffset: number) =>
    nav.createNavigationLink(
      nav.urlFunctions.createReservationList({
        timeslotDaySince: dayjs().add(dayOffset, 'day').startOf('day').toISOString(),
        timeslotDayTill: dayjs().add(dayOffset, 'day').endOf('day').toISOString(),
        ...openStatesParam,
      }),
    );
  const reservationsRangeLink = nav.createNavigationLink(
    nav.urlFunctions.createReservationList({
      timeslotDaySince: dayjs().subtract(1, 'day').startOf('day').toISOString(),
      timeslotDayTill: dayjs().add(1, 'day').endOf('day').toISOString(),
      ...openStatesParam,
    }),
  );
  // Supplier-notification list filtered to those without a linked expedition, with the current
  // company preselected as the consignee (customer).
  const notificationsLink = nav.createNavigationLink(nav.urlFunctions.createSupplierNotificationList({ onlyWithoutExpedition: true, customerId: loggedUser?.companyId }));
  const demandsLink = nav.createNavigationLink(
    nav.urlFunctions.createCarrierDemandsList({ pseudoStates: [PseudoStatesEnum.ASSIGNING, PseudoStatesEnum.OFFERED, PseudoStatesEnum.REJECTED] }),
  );
  const multiDemandsLink = nav.createNavigationLink(nav.urlFunctions.createCarrierMultiDemandsList({ groupDemandState: ['NEW'] }));
  const shipmentsWaitingForDocumentsLink = nav.createNavigationLink(nav.urlFunctions.createCarrierShipmentsList({ carrierExpeditionStatus: CarrierExpeditionStatusEnum.WAITING_FOR_DOCS }));
  const shipmentsInProgressLink = nav.createNavigationLink(nav.urlFunctions.createCarrierShipmentsList({ carrierExpeditionStatus: CarrierExpeditionStatusEnum.READY_TO_SHIPMENT }));
  const shipmentsActiveLink = nav.createNavigationLink(nav.urlFunctions.createCarrierShipmentsList({ carrierExpeditionStatus: CarrierExpeditionStatusEnum.ACTIVE }));

  // Creation routes for the "+ New" buttons in the summary row.
  const createExpeditionLink = nav.createNavigationLink(nav.urlFunctions.createExpedition());
  const createNotificationLink = nav.createNavigationLink(nav.urlFunctions.createSupplierNotificationCreate());
  const snCreateFlag = useSecuredFlag('dashboard-tasks-sn-create', {
    kind: 'visibility',
    description: 'Create supplier notification button',
    permissions: [PermissionTypesEnum.PERMISSION_SN_EFXX_CREATE],
  });

  // Opens the create-reservation sidebar (no standalone create page exists).
  const openNewReservation = () =>
    setCreateTimeslotRequest({ applicationId: null, expeditionId: null, companyLocationId: null, companyLocationCustomerId: null, expeditionLocationId: null, preferredDate: '', timezoneId: null });

  // Summary totals = sum of the per-task breakdown shown below (optional-chained: SecuredArea
  // children evaluate eagerly even when the area is hidden).
  const exp = consignee?.expeditions;
  // Sum every status the "Active expeditions" link selects (ACTIVE_EXPEDITION_STATUSES) so the count
  // matches the linked list — including the `new` and `assigned` buckets the link also covers.
  const expeditionsTotal = exp ? exp.new + exp.assigning + exp.assigned + exp.waitingForDocuments + exp.inProgress + exp.active : undefined;
  const res = consignee?.reservations;
  const reservationsTotal = res ? res.openYesterday + res.openToday + res.openTomorrow : undefined;

  const newLabel = tr('dashboard.tasks.new', 'New');
  // First (category) line of each breakdown tile.
  const catShipments = tr('dashboard.tasks.category.shipments', 'My shipments');
  const catReservations = tr('dashboard.tasks.category.reservations', 'Reservations');
  const catNotifications = tr('dashboard.tasks.category.notifications', 'Notifications');

  const showConsignee = expeditionFlag.granted || reservationFlag.granted || notificationFlag.granted;
  const showCarrier = carrierFlag.granted;

  if (!showConsignee && !showCarrier) return null;

  return (
    <>
    <div className="flex flex-col gap-4">
      {showConsignee && (
        <div className="flex flex-col gap-3">
          <Heading title={tr('dashboard.tasks.consigneeTitle', 'My consignee tasks')} headingVariant={'h3'} />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <SecuredArea id="dashboard-tasks-expedition-summary" visible={expeditionFlag} label="Active expeditions" description="Dashboard tile: number of active shipments">
              <DashboardSummaryTile label={tr('dashboard.tasks.activeExpeditions', 'Active expeditions')} count={expeditionsTotal} listTo={activeExpeditionsLink} createTo={createExpeditionLink} createLabel={newLabel} />
            </SecuredArea>
            <SecuredArea id="dashboard-tasks-reservation-summary" visible={reservationFlag} label="Open reservations" description="Dashboard tile: number of open reservations">
              <DashboardSummaryTile label={tr('dashboard.tasks.openReservations', 'Open reservations')} count={reservationsTotal} listTo={reservationsRangeLink} onCreate={openNewReservation} createLabel={newLabel} />
            </SecuredArea>
            <SecuredArea id="dashboard-tasks-notification-summary" visible={notificationFlag} label="Notifications" description="Dashboard tile: supplier notifications without a shipment">
              <DashboardSummaryTile
                label={tr('dashboard.tasks.notifications', 'Notifications')}
                count={consignee?.notificationsWithoutExpedition}
                listTo={notificationsLink}
                createTo={snCreateFlag.granted ? createNotificationLink : undefined}
                createLabel={newLabel}
              />
            </SecuredArea>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <SecuredArea id="dashboard-tasks-expedition-breakdown" visible={expeditionFlag} label="Expedition tasks" description="Dashboard tiles: shipments by stage, such as choosing carrier, carrier assigned and waiting for documents">
              <DashboardCountTile category={catShipments} label={tr('dashboard.tasks.exp.assigning', 'Choosing carrier')} count={consignee?.expeditions?.assigning} to={expLink([StatusEnumsEnum.ASSIGNING])} />
              <DashboardCountTile category={catShipments} label={tr('dashboard.tasks.exp.waitingForDocuments', 'Waiting for documents')} count={consignee?.expeditions?.waitingForDocuments} to={expLink([StatusEnumsEnum.WAITING_FOR_DOCS])} />
              <DashboardCountTile category={catShipments} label={tr('dashboard.tasks.exp.assigned', 'Carrier assigned')} count={consignee?.expeditions?.assigned} to={expLink([StatusEnumsEnum.ASSIGNED])} />
              <DashboardCountTile category={catShipments} label={tr('dashboard.tasks.exp.readyToShiment', 'Ready to shipment')} count={consignee?.expeditions?.inProgress} to={expLink([StatusEnumsEnum.READY_TO_SHIPMENT])} />
              <DashboardCountTile category={catShipments} label={tr('dashboard.tasks.exp.active', 'In progress')} count={consignee?.expeditions?.active} to={expLink([StatusEnumsEnum.ACTIVE])} />
            </SecuredArea>
            <SecuredArea id="dashboard-tasks-reservation-breakdown" visible={reservationFlag} label="Reservation tasks" description="Dashboard tiles: open reservations for yesterday, today and tomorrow">
              <DashboardCountTile category={catReservations} label={tr('dashboard.tasks.res.yesterday', 'Yesterday')} count={consignee?.reservations?.openYesterday} to={reservationDayLink(-1)} />
              <DashboardCountTile category={catReservations} label={tr('dashboard.tasks.res.today', 'Today')} count={consignee?.reservations?.openToday} to={reservationDayLink(0)} />
              <DashboardCountTile category={catReservations} label={tr('dashboard.tasks.res.tomorrow', 'Tomorrow')} count={consignee?.reservations?.openTomorrow} to={reservationDayLink(1)} />
            </SecuredArea>
            <SecuredArea id="dashboard-tasks-notification-breakdown" visible={notificationFlag} label="Notification tasks" description="Dashboard tile: supplier notifications without a shipment">
              <DashboardCountTile category={catNotifications} label={tr('dashboard.tasks.notificationsWithoutExpedition', 'Without expedition')} count={consignee?.notificationsWithoutExpedition} to={notificationsLink} />
            </SecuredArea>
          </div>
        </div>
      )}

      {showCarrier && (
        <div className="flex flex-col gap-3">
          <Heading title={tr('dashboard.tasks.carrierTitle', 'My carrier tasks')} headingVariant={'h3'} />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <SecuredArea id="dashboard-tasks-carrier" visible={carrierFlag} label="Carrier tasks" description="Dashboard tiles for a carrier: open demands, open group demands and shipments waiting for documents">
              <DashboardCountTile label={tr('dashboard.tasks.carrier.openSingleDemands', 'Open demands')} count={carrier?.openSingleDemands} to={demandsLink} />
              <DashboardCountTile label={tr('dashboard.tasks.carrier.openGroupDemands', 'Open group demands')} count={carrier?.openGroupDemands} to={multiDemandsLink} />
              <DashboardCountTile label={tr('dashboard.tasks.carrier.waitingForDocuments', 'Waiting for documents')} count={carrier?.expeditionsWaitingForDocuments} to={shipmentsWaitingForDocumentsLink} />
              <DashboardCountTile label={tr('dashboard.tasks.carrier.inProgress', 'In progress')} count={carrier?.expeditionsInProgress} to={shipmentsInProgressLink} />
              <DashboardCountTile label={tr('dashboard.tasks.carrier.active', 'Active')} count={carrier?.expeditionsActive} to={shipmentsActiveLink} />
            </SecuredArea>
          </div>
        </div>
      )}
    </div>
      {createTimeslotRequest && <CreateTimeslotSidebar onContinue={() => getDashboardSectionConsigneeTasks(data => setConsignee(data))} headerDisabled={false} />}
    </>
  );
};

export default TaskTiles;